import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import config from '../../../config';
import styles from '../Settings.module.css';

interface Ingredient {
  id: string;
  normalized_name: string;
  name?: string; // For compatibility, will be set to normalized_name
  category?: string;
  unit?: string;
}

interface IngredientPreference {
  id: number;
  ingredient_id: number;
  preference_type: 'love' | 'like' | 'dislike' | 'avoid' | 'allergy' | 'intolerance';
  ingredient: {
    id: number;
    name: string;
  };
}

interface IngredientsTabProps {
  onNotification: (type: 'success' | 'error', message: string) => void;
}

const IngredientsTab: React.FC<IngredientsTabProps> = ({ onNotification }) => {
  const { user, saveUserPreferencesToStorage } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Ingredient[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Use user object as single source of truth
  const ingredientPreferences = user?.preferences?.ingredientPreferences || [];

  // Helper function to convert text to title case
  const toTitleCase = (text: string): string => {
    if (!text) return text;
    
    return text
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Track when user object changes (debugging can be enabled if needed)
  // useEffect(() => {
  //   console.log('IngredientsTab - user object changed:', user);
  //   console.log('IngredientsTab - ingredientPreferences from user object:', ingredientPreferences);
  // }, [user, ingredientPreferences]);

  const searchIngredients = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const url = `${config.API_BASE_URL}/ingredient-normalizations/search`;
      const response = await axios.get(url, {
        params: { q: query, limit: 5 },
        headers: {
          'Authorization': `Bearer ${user?.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      setSearchResults(response.data);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Settings error searching ingredients:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        console.error('Settings error details:', (error as any).response?.data);
      }
      onNotification('error', 'Failed to search ingredients');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchIngredients(query);
  };

  const addIngredientPreference = async (ingredient: Ingredient, preferenceType: 'love' | 'like' | 'dislike' | 'avoid' | 'allergy' | 'intolerance') => {
    if (!user) return;

    // Check if user already has a preference for this ingredient
    // Convert both IDs to strings for comparison to avoid type issues
    const existingPreference = ingredientPreferences.find(
      pref => pref.ingredient_id.toString() === ingredient.id
    );

    let updated: IngredientPreference[];
    let tempId: number | null = null;
    
    if (existingPreference) {
      // User already has a preference for this ingredient, update it
      console.log(`Updating existing preference for ${ingredient.normalized_name} from ${existingPreference.preference_type} to ${preferenceType}`);
      
      updated = ingredientPreferences.map(pref => 
        pref.ingredient_id.toString() === ingredient.id
          ? { ...pref, preference_type: preferenceType }
          : pref
      );
    } else {
      // No existing preference, create new one
      tempId = Date.now();
      const optimisticPreference: IngredientPreference = {
        id: tempId,
        ingredient_id: parseInt(ingredient.id),
        preference_type: preferenceType,
        ingredient: {
          id: parseInt(ingredient.id),
          name: ingredient.normalized_name
        }
      };
      
      updated = [...ingredientPreferences, optimisticPreference];
    }

    // Optimistically update user object immediately
    saveUserPreferencesToStorage({
      dietary: user?.preferences?.dietary || [],
      healthConcerns: user?.preferences?.healthConcerns || [],
      ingredientPreferences: updated
    });
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);

    try {
      const response = await axios.post(`${config.API_BASE_URL}/user/ingredient-preferences`, {
        ingredient_normalization_id: ingredient.id,
        preference_type: preferenceType
      }, {
        headers: {
          'Authorization': `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      // Update with real data from server
      const finalUpdated = updated.map((pref: IngredientPreference) => {
        if (existingPreference) {
          // For updates, replace the existing preference with server data
          return pref.ingredient_id.toString() === ingredient.id
            ? { 
                ...response.data, 
                ingredient: { 
                  id: parseInt(ingredient.id), 
                  name: ingredient.normalized_name 
                }
              }
            : pref;
        } else {
          // For new preferences, replace the temporary ID with server data
          return pref.id === tempId 
            ? { 
                ...response.data, 
                ingredient: { 
                  id: parseInt(ingredient.id), 
                  name: ingredient.normalized_name 
                }
              }
            : pref;
        }
      });
      
      saveUserPreferencesToStorage({
        dietary: user?.preferences?.dietary || [],
        healthConcerns: user?.preferences?.healthConcerns || [],
        ingredientPreferences: finalUpdated
      });
      
      const action = existingPreference ? 'Updated' : 'Added';
      onNotification('success', `${action} ${ingredient.normalized_name} as ${preferenceType}`);
    } catch (error) {
      console.error('Error adding/updating ingredient preference:', error);
      
      // Revert the optimistic update if API call fails
      if (existingPreference) {
        // Revert to original state
        saveUserPreferencesToStorage({
          dietary: user?.preferences?.dietary || [],
          healthConcerns: user?.preferences?.healthConcerns || [],
          ingredientPreferences: ingredientPreferences
        });
      } else {
        // Remove the temporary preference
        const reverted = ingredientPreferences.filter((pref: IngredientPreference) => pref.id !== tempId);
        saveUserPreferencesToStorage({
          dietary: user?.preferences?.dietary || [],
          healthConcerns: user?.preferences?.healthConcerns || [],
          ingredientPreferences: reverted
        });
      }
      
      onNotification('error', 'Failed to add/update ingredient preference');
    }
  };

  const removeIngredientPreference = async (preferenceId: string) => {
    if (!user) return;

    // Store original state for potential rollback
    const originalState = ingredientPreferences;
    
    // Optimistically update user object immediately
    const updated = ingredientPreferences.filter(p => p.id.toString() !== preferenceId);
    
    saveUserPreferencesToStorage({
      dietary: user?.preferences?.dietary || [],
      healthConcerns: user?.preferences?.healthConcerns || [],
      ingredientPreferences: updated
    });

    try {
      await axios.delete(`${config.API_BASE_URL}/user/ingredient-preferences/${preferenceId}`, {
        headers: {
          'Authorization': `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      onNotification('success', 'Ingredient preference removed');
    } catch (error) {
      console.error('Error removing ingredient preference:', error);
      
      // Revert the optimistic update if API call fails
      saveUserPreferencesToStorage({
        dietary: user?.preferences?.dietary || [],
        healthConcerns: user?.preferences?.healthConcerns || [],
        ingredientPreferences: originalState
      });
      
      onNotification('error', 'Failed to remove ingredient preference');
    }
  };

  const getLovesList = () => {
    return ingredientPreferences
      .filter((p: IngredientPreference) => p.preference_type === 'love')
      .map((p: IngredientPreference) => p);
  };

  const getLikesList = () => {
    return ingredientPreferences
      .filter((p: IngredientPreference) => p.preference_type === 'like')
      .map((p: IngredientPreference) => p);
  };

  const getDislikesList = () => {
    return ingredientPreferences
      .filter((p: IngredientPreference) => p.preference_type === 'dislike')
      .map((p: IngredientPreference) => p);
  };

  const getAvoidsList = () => {
    return ingredientPreferences
      .filter((p: IngredientPreference) => p.preference_type === 'avoid')
      .map((p: IngredientPreference) => p);
  };

  const getAllergiesList = () => {
    return ingredientPreferences
      .filter((p: IngredientPreference) => p.preference_type === 'allergy')
      .map((p: IngredientPreference) => p);
  };

  const getIntolerancesList = () => {
    return ingredientPreferences
      .filter((p: IngredientPreference) => p.preference_type === 'intolerance')
      .map((p: IngredientPreference) => p);
  };


  return (
    <div className={styles.tabContent}>
      <div className={styles.sectionHeader}>
        <h3>Ingredient Preferences</h3>
        <p className={styles.sectionDescription}>
          Tell us about ingredients you like, dislike, or are allergic to. 
          This helps us personalize your recipe recommendations.
        </p>
      </div>
      
      {/* Search Section */}
      <div className={styles.searchSection}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search for ingredients..."
            value={searchQuery}
            onChange={handleSearchChange}
            className={styles.searchInput}
          />
          {isSearching && <div className={styles.searchSpinner}>Searching...</div>}
        </div>

        {/* Search Results */}
        {showSearchResults && searchResults.length > 0 && (
          <div className={styles.searchResults}>
            {searchResults.map((ingredient) => (
              <div key={ingredient.id} className={styles.ingredientCard}>
                <div className={styles.ingredientHeader}>
                  <span className={styles.ingredientName}>{ingredient.name || ingredient.normalized_name}</span>
                </div>
                  <div className={styles.preferenceActions}>
                    <button
                      onClick={() => addIngredientPreference(ingredient, 'love')}
                      className={`${styles.preferenceAction} ${styles.loveAction}`}
                    >
                      <span className={styles.actionLabel}>Love</span>
                    </button>
                    <button
                      onClick={() => addIngredientPreference(ingredient, 'like')}
                      className={`${styles.preferenceAction} ${styles.likeAction}`}
                    >
                      <span className={styles.actionLabel}>Like</span>
                    </button>
                    <button
                      onClick={() => addIngredientPreference(ingredient, 'dislike')}
                      className={`${styles.preferenceAction} ${styles.dislikeAction}`}
                    >
                      <span className={styles.actionLabel}>Dislike</span>
                    </button>
                    <button
                      onClick={() => addIngredientPreference(ingredient, 'avoid')}
                      className={`${styles.preferenceAction} ${styles.avoidAction}`}
                    >
                      <span className={styles.actionLabel}>Avoid</span>
                    </button>
                    <button
                      onClick={() => addIngredientPreference(ingredient, 'allergy')}
                      className={`${styles.preferenceAction} ${styles.allergyAction}`}
                    >
                      <span className={styles.actionLabel}>Allergy</span>
                    </button>
                    <button
                      onClick={() => addIngredientPreference(ingredient, 'intolerance')}
                      className={`${styles.preferenceAction} ${styles.intoleranceAction}`}
                    >
                      <span className={styles.actionLabel}>Intolerance</span>
                    </button>
                  </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Current Preferences */}
      <div className={styles.preferencesSection}>
        {/* Likes */}
        {getLikesList().length > 0 && (
          <div className={styles.preferenceCategory}>
            <h4>Likes</h4>
            <div className={styles.preferenceTags}>
              {getLikesList().map((preference) => (
                <div key={preference.id} className={`${styles.preferenceTag} ${styles[preference.preference_type]}`}>
                  <span className={styles.tagType}>
                    {preference.preference_type.charAt(0).toUpperCase() + preference.preference_type.slice(1)}
                  </span>
                  <span className={styles.tagLabel}>
                    {toTitleCase(preference.ingredient.name || 'Unknown Ingredient')}
                  </span>
                  <button
                    onClick={() => removeIngredientPreference(preference.id.toString())}
                    className={styles.removeTag}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dislikes */}
        {getDislikesList().length > 0 && (
          <div className={styles.preferenceCategory}>
            <h4>Dislikes</h4>
            <div className={styles.preferenceTags}>
              {getDislikesList().map((preference) => (
                <div key={preference.id} className={`${styles.preferenceTag} ${styles[preference.preference_type]}`}>
                  <span className={styles.tagType}>
                    {preference.preference_type.charAt(0).toUpperCase() + preference.preference_type.slice(1)}
                  </span>
                  <span className={styles.tagLabel}>
                    {toTitleCase(preference.ingredient.name || 'Unknown Ingredient')}
                  </span>
                  <button
                    onClick={() => removeIngredientPreference(preference.id.toString())}
                    className={styles.removeTag}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Avoids */}
        {getAvoidsList().length > 0 && (
          <div className={styles.preferenceCategory}>
            <h4>Avoids</h4>
            <div className={styles.preferenceTags}>
              {getAvoidsList().map((preference) => (
                <div key={preference.id} className={`${styles.preferenceTag} ${styles[preference.preference_type]}`}>
                  <span className={styles.tagType}>
                    {preference.preference_type.charAt(0).toUpperCase() + preference.preference_type.slice(1)}
                  </span>
                  <span className={styles.tagLabel}>
                    {toTitleCase(preference.ingredient.name || 'Unknown Ingredient')}
                  </span>
                  <button
                    onClick={() => removeIngredientPreference(preference.id.toString())}
                    className={styles.removeTag}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Loves */}
        {getLovesList().length > 0 && (
          <div className={styles.preferenceCategory}>
            <h4>Loves</h4>
            <div className={styles.preferenceTags}>
              {getLovesList().map((preference) => (
                <div key={preference.id} className={`${styles.preferenceTag} ${styles[preference.preference_type]}`}>
                  <span className={styles.tagType}>
                    {preference.preference_type.charAt(0).toUpperCase() + preference.preference_type.slice(1)}
                  </span>
                  <span className={styles.tagLabel}>
                    {toTitleCase(preference.ingredient.name || 'Unknown Ingredient')}
                  </span>
                  <button
                    onClick={() => removeIngredientPreference(preference.id.toString())}
                    className={styles.removeTag}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Allergies */}
        {getAllergiesList().length > 0 && (
          <div className={styles.preferenceCategory}>
            <h4>Allergies</h4>
            <div className={styles.preferenceTags}>
              {getAllergiesList().map((preference) => (
                <div key={preference.id} className={`${styles.preferenceTag} ${styles[preference.preference_type]}`}>
                  <span className={styles.tagType}>
                    {preference.preference_type.charAt(0).toUpperCase() + preference.preference_type.slice(1)}
                  </span>
                  <span className={styles.tagLabel}>
                    {toTitleCase(preference.ingredient.name || 'Unknown Ingredient')}
                  </span>
                  <button
                    onClick={() => removeIngredientPreference(preference.id.toString())}
                    className={styles.removeTag}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Intolerances */}
        {getIntolerancesList().length > 0 && (
          <div className={styles.preferenceCategory}>
            <h4>Intolerances</h4>
            <div className={styles.preferenceTags}>
              {getIntolerancesList().map((preference) => (
                <div key={preference.id} className={`${styles.preferenceTag} ${styles[preference.preference_type]}`}>
                  <span className={styles.tagType}>
                    {preference.preference_type.charAt(0).toUpperCase() + preference.preference_type.slice(1)}
                  </span>
                  <span className={styles.tagLabel}>
                    {toTitleCase(preference.ingredient.name || 'Unknown Ingredient')}
                  </span>
                  <button
                    onClick={() => removeIngredientPreference(preference.id.toString())}
                    className={styles.removeTag}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IngredientsTab;
